<script lang="ts">
import { ProgramTower } from '@/types/tower/v2/Tower';
import { fabric } from 'fabric';
import { AppState } from '@/store';
import { useStore } from 'vuex';
import TowerVisualizerCanvas from './TowerVisualizerCanvas.vue';
import UILayer from './UILayer.vue';
import { getElementHeight, getElementWidth } from '@/utilities/DOM';
import { TowerCardData } from '../tower_dashboard/types/tower-dashboard';

export default {
  name: 'TowerVisualizerPage',
  components: {
    TowerVisualizerCanvas,
    UILayer,
  },
};

export const UIColorDefaults = {
  background: 'white',
  primary: 'taupe-primary',
  secondary: 'taupe-secondary',
};
</script>

<script setup lang="ts">
import { ref, onMounted, onBeforeMount, onBeforeUnmount, provide } from 'vue';
import { towerService } from '@/services/v2/TowerService';
import { UIColors } from '@/views/tower_visualizer/types/TowerVisualizerPage';

const props = withDefaults(
  defineProps<{
    towerId?: string;
    serializedTower?: string;
    accountId: string;
    uiColors?: UIColors;
  }>(),
  {
    uiColors: () => UIColorDefaults,
  },
);

const pageRef = ref<HTMLElement>();
const resizeObserver = ref<ResizeObserver>({} as ResizeObserver);
const width = ref<number>(pageRef.value?.clientWidth ?? 0);
const height = ref<number>(pageRef.value?.clientHeight ?? 0);
const store = useStore<AppState>();

// ----------------
// provider stuff
// ----------------
const canvas = ref<fabric.Canvas>();
const towerChanged = ref<boolean>(false);
const accountId = ref<number>(Number(props.accountId));
const tower = ref<ProgramTower>();
const selectedTower = ref<TowerCardData>();

provide('towerInfo', {
  canvas,
  towerChanged,
  accountId,
  tower,
  selectedTower,
});

onBeforeMount(async () => {
  // get tower by id
  if (props.towerId) {
    tower.value = (
      await towerService.getTowers({
        towerIds: props.towerId ? [props.towerId] : [],
      })
    )[0];
  } else if (props.serializedTower) {
    tower.value = { towerObject: JSON.parse(props.serializedTower) } as ProgramTower;
  }
});

onMounted(async () => {
  // handle resizing of the page
  resizeObserver.value = new ResizeObserver(() => {
    if (pageRef.value) {
      const pageHeight = window.innerHeight - (store.state.resizerRegistry['header'].height ?? 0);
      pageRef.value.style.height = `${pageHeight}px`;

      width.value = getElementWidth(pageRef.value);
      height.value = getElementHeight(pageRef.value);
    }
  });
  resizeObserver.value.observe(pageRef.value as Element);
});

onBeforeUnmount(() => {
  resizeObserver.value.disconnect();
});
</script>

<template>
  <div ref="pageRef" class="tw:absolute tw:w-full" :class="`tw:bg-${uiColors.background}`">
    <UILayer :ui-colors="uiColors" />
    <TowerVisualizerCanvas v-if="tower && width > 0 && height > 0" :width="width" :height="height" :tower="tower" />
  </div>
</template>

<style>
:root {
  --zindex-canvas: 1;
  --zindex-wysiwyg: 2;
  --zindex-layerui: 3;
}
</style>
